// frontend/src/components/PlaySession.js
// DROP-IN replacement for your current PlaySession.js.
// Fixes CI build failure by making heartbeat functions stable and including deps correctly.
// Also hardens cleanup to avoid stale sessionId issues and socket leaks.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { campaignsAPI, liveSessionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { io } from 'socket.io-client';
import './PlaySession.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const HEARTBEAT_MS = 30 * 1000;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function PlaySession() {
  const { user } = useAuth();
  const { campaignId } = useParams();
  const q = useQuery();

  const [campaign, setCampaign] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [myCharacters, setMyCharacters] = useState([]);

  const [liveSession, setLiveSession] = useState(null); // { id, joinCode, status, createdAt, campaignId }
  const [participants, setParticipants] = useState([]);

  const [chat, setChat] = useState([]);
  const [chatText, setChatText] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);

  // keep latest session id for cleanups without depending on liveSession in callbacks
  const liveSessionIdRef = useRef(null);
  useEffect(() => {
    liveSessionIdRef.current = liveSession?.id || null;
  }, [liveSession?.id]);

  const isOwnerGM = useMemo(() => {
    if (!campaign || !user) return false;
    const role = (user?.role || '').toString().trim().toUpperCase();
    const gmish = role === 'GM' || role === 'GAME MASTER' || role === 'GAMEMASTER' || role === 'ADMIN';
    return gmish && String(campaign.gmId).toLowerCase() === String(user.id).toLowerCase();
  }, [campaign, user]);

  const shouldLaunch = q.get('launch') === '1';

  // Ensure heartbeat interval is cleared (stable function)
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // Start heartbeat emitting while socket is connected (stable function)
  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      const s = socketRef.current;
      if (s && s.connected) {
        s.emit('session:heartbeat');
      }
    }, HEARTBEAT_MS);
  }, [stopHeartbeat]);

  // Cleanly leave a live session room (stable function)
  const leaveLiveSession = useCallback((sessionId) => {
    const s = socketRef.current;
    if (!s || !sessionId) return;
    try {
      s.emit('leaveSession', { sessionId });
    } catch {
      // ignore
    }
  }, []);

  // Load campaign + characters + (launch OR get active live session)
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 1) Campaign + characters
        const res = await campaignsAPI.getCampaignDetails(campaignId);
        if (!mounted) return;

        const c = res.data?.campaign || null;
        const chars = res.data?.characters || [];

        setCampaign(c);
        setCharacters(chars);

        const mine = chars.filter(
          (ch) => String(ch.userId).toLowerCase() === String(user?.id || '').toLowerCase()
        );
        setMyCharacters(mine);

        // 2) Live session: GM launch OR just fetch active
        let session = null;

        if (shouldLaunch) {
          const launched = await liveSessionsAPI.launch(campaignId);
          session = launched.data?.session || null;
        } else {
          const active = await liveSessionsAPI.getActive(campaignId);
          session = active.data?.session || null;
        }

        if (!mounted) return;
        setLiveSession(session);
      } catch (e) {
        if (!mounted) return;
        console.error('Failed to load play session:', e);
        setError(e?.response?.data?.error || 'Failed to load play session');
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    if (campaignId) load();

    return () => {
      mounted = false;
    };
  }, [campaignId, shouldLaunch, user?.id]);

  // Join session (API) + connect socket
  useEffect(() => {
    let cancelled = false;

    const connect = async () => {
      if (!liveSession?.id) return;

      // Bind the player’s first character automatically for now (you can add a selector later)
      const myCharId = myCharacters?.[0]?.id || null;

      // Ensure previous socket (if any) is gone before creating a new one
      if (socketRef.current) {
        try {
          leaveLiveSession(liveSessionIdRef.current);
        } catch {}
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      stopHeartbeat();

      // Socket first: connect early so we can immediately join room + receive broadcasts
      const token = localStorage.getItem('token');
      const socket = io(API_BASE, {
        transports: ['websocket'],
        auth: { token }
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        const sid = liveSessionIdRef.current;
        if (!sid) return;

        // Join the live session room
        socket.emit('joinSession', { sessionId: sid });

        // Start heartbeat to keep "in game session" state fresh on server
        startHeartbeat();
      });

      socket.on('session:participants', (payload) => {
        if (cancelled) return;
        setParticipants(payload?.participants || []);
      });

      socket.on('chat:message', (msg) => {
        if (cancelled) return;
        setChat((prev) => [...prev, msg]);
      });

      socket.on('disconnect', () => {
        stopHeartbeat();
      });

      socket.on('connect_error', (err) => {
        stopHeartbeat();
        console.error('Socket connect_error:', err?.message || err);
      });

      // Then perform REST join (persists participant binding)
      try {
        await liveSessionsAPI.join(liveSession.id, myCharId);

        // After join succeeds, refresh our roster entry so characterName/role are correct immediately
        if (!cancelled) {
          socket.emit('session:refreshMe', { sessionId: liveSession.id });
        }
      } catch (e) {
        // Non-fatal: you can still stay connected; join endpoint mainly persists binding details
        console.warn('Join live session failed (continuing):', e?.response?.data || e?.message);
      }
    };

    connect();

    return () => {
      cancelled = true;

      // Clean leave + stop heartbeat + disconnect
      stopHeartbeat();

      const sid = liveSessionIdRef.current;
      if (socketRef.current) {
        try {
          if (sid) socketRef.current.emit('leaveSession', { sessionId: sid });
        } catch {}
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [liveSession?.id, myCharacters, leaveLiveSession, startHeartbeat, stopHeartbeat]);

  const sendChat = useCallback(() => {
    const text = (chatText || '').trim();
    const s = socketRef.current;
    const sid = liveSession?.id;

    if (!text || !s || !sid) return;

    s.emit('chat:send', { sessionId: sid, text });
    setChatText('');
  }, [chatText, liveSession?.id]);

  const endSession = useCallback(async () => {
    if (!liveSession?.id) return;

    try {
      // Tell server we are leaving first (unpause immediately, even before socket disconnect)
      try {
        if (socketRef.current) {
          socketRef.current.emit('leaveSession', { sessionId: liveSession.id });
        }
      } catch {}

      await liveSessionsAPI.end(liveSession.id);

      setLiveSession(null);
      setParticipants([]);
      setChat([]);

      stopHeartbeat();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to end session');
    }
  }, [liveSession?.id, stopHeartbeat]);

  const roleClass = (role) => {
    const r = (role || '').toString().trim().toUpperCase();
    return r === 'GM' || r === 'ADMIN' ? 'is-gm' : '';
  };

  return (
    <div className="play-session-page" style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '16px'
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Play Session</h2>
          {campaign?.name && <p style={{ margin: '6px 0 0', opacity: 0.8 }}>{campaign.name}</p>}
        </div>
        <Link to="/dashboard" className="btn-secondary">
          Back to Dashboard
        </Link>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ color: '#ff8b8b' }}>{error}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '16px' }}>
          <div className="campaign-card" style={{ padding: '16px' }}>
            <h3 style={{ marginTop: 0 }}>Live Session</h3>

            {!liveSession ? (
              <div style={{ opacity: 0.9 }}>
                <p style={{ marginTop: 0 }}>No live session is active right now.</p>
                {isOwnerGM ? (
                  <p style={{ opacity: 0.85 }}>Launch this campaign from your dashboard to create a live session.</p>
                ) : (
                  <p style={{ opacity: 0.85 }}>Waiting for the GM to launch the campaign…</p>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ padding: '8px 10px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10 }}>
                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>Join Code</div>
                    <div style={{ fontSize: '1.35rem', fontWeight: 800, letterSpacing: '0.06em' }}>
                      {liveSession.joinCode}
                    </div>
                  </div>
                  <div style={{ opacity: 0.85 }}>
                    <div style={{ fontSize: '0.85rem' }}>Status</div>
                    <div style={{ fontWeight: 700 }}>{liveSession.status}</div>
                  </div>

                  {isOwnerGM && (
                    <button className="btn-secondary" onClick={endSession}>
                      End Session
                    </button>
                  )}
                </div>

                <hr style={{ margin: '16px 0', opacity: 0.2 }} />

                <h4 style={{ margin: '0 0 10px' }}>Table Chat</h4>
                <div
                  style={{
                    height: 220,
                    overflow: 'auto',
                    padding: 10,
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 10,
                    background: 'rgba(0,0,0,0.15)'
                  }}
                >
                  {chat.length === 0 ? (
                    <div style={{ opacity: 0.75 }}>No messages yet.</div>
                  ) : (
                    chat.map((m, idx) => (
                      <div key={idx} style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 700 }}>{m.username}</span>
                        <span style={{ opacity: 0.6 }}> · </span>
                        <span style={{ opacity: 0.95 }}>{m.text}</span>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <input
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') sendChat();
                    }}
                    placeholder="Say something…"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(0,0,0,0.2)',
                      color: 'white'
                    }}
                  />
                  <button className="btn-primary" onClick={sendChat}>
                    Send
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="campaign-card" style={{ padding: '16px' }}>
            {/* Styled Players header block (card feel) */}
            <div className="session-players-header">
              <h3 style={{ marginTop: 0 }}>Players</h3>
              {liveSession?.id && <div className="session-players-count">Connected: {participants.length}</div>}
            </div>

            {liveSession?.id ? (
              <>
                {participants.length === 0 ? (
                  <p style={{ opacity: 0.8 }}>No one connected yet.</p>
                ) : (
                  <div className="session-players-list">
                    {participants.map((p) => (
                      <div key={p.userId} className="session-player-row">
                        <div className="session-player-left">
                          <div className="session-player-dot" />
                          <div className="session-player-names">
                            <div className="session-player-name">{p.username}</div>
                            <div className="session-player-character">
                              {p.characterName ? `Playing: ${p.characterName}` : 'No character bound'}
                            </div>
                          </div>
                        </div>

                        <div className={`session-player-role ${roleClass(p.role)}`}>{p.role || 'PLAYER'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p style={{ opacity: 0.8 }}>Players will appear here once a live session is active.</p>
            )}

            <hr style={{ margin: '16px 0', opacity: 0.2 }} />

            <h4 style={{ margin: '0 0 10px' }}>Campaign Characters</h4>
            {characters.length === 0 ? (
              <p style={{ opacity: 0.8 }}>No characters joined yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {characters.map((ch) => (
                  <div key={ch.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{ch.name}</div>
                      <div style={{ opacity: 0.8, fontSize: '0.92rem' }}>
                        {ch.characterType} • Lvl {ch.level} • {ch.availableXp} XP
                      </div>
                    </div>
                    <div style={{ opacity: 0.85, whiteSpace: 'nowrap' }}>{ch.playerName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaySession;
